import { supabase } from '../supabaseClient';
import {
  validateImage,
  getImageDimensions,
  normalizeImage,
} from '../utils/imageValidation';
import { checkRateLimit } from '../utils/rateLimiter';
import { logError } from './errorLogger';

// Mobile networks (and the proxy in front of Storage) occasionally drop an
// upload with a 5xx — seen in production as a one-off "HTTP 520" that
// succeeded seconds later when the guest retried by hand. Retrying here
// saves the guest a failed photo. Only transient failures are retried:
// 4xx responses (RLS/payment/cap rejections, bad file) fail immediately.
const RETRY_DELAYS_MS = [800, 2000];

const isTransientStorageError = (error) => {
  const status = Number(error?.status ?? error?.statusCode ?? error?.originalError?.status);
  if (Number.isFinite(status) && status >= 500) return true;
  // No usable status = the request never completed (Safari: "Load failed",
  // Chrome: "Failed to fetch"), or a non-JSON 5xx body surfaced as "HTTP 5xx".
  return /failed to fetch|load failed|network|timed? ?out|HTTP 5\d\d/i.test(error?.message || '');
};

const isAlreadyExistsError = (error) =>
  String(error?.statusCode ?? error?.status) === '409' ||
  /already exists|duplicate/i.test(error?.message || '');

// `upload` is the single storage call. Because the storage path is unique
// per attempt-set (timestamp + random), a 409 on a retry means an earlier
// attempt actually landed and only its response was lost — that's success.
export const uploadWithRetry = async (upload, delays = RETRY_DELAYS_MS) => {
  let result = await upload();

  for (let attempt = 0; result.error && attempt < delays.length; attempt++) {
    if (!isTransientStorageError(result.error)) break;
    await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
    result = await upload();
    if (result.error && isAlreadyExistsError(result.error)) {
      return { data: result.data, error: null };
    }
  }

  return result;
};

export const uploadImage = async (
  file,
  eventId,
  userId,
  onProgress = null,
  uploaderName = null
) => {
  // Tracks which step we were on so a failure log says exactly where it
  // broke (cap check vs. storage upload vs. DB insert), not just "it failed".
  let stage = 'validate';

  try {
    checkRateLimit();

    const validationErrors = validateImage(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(', '));
    }

    // Fail fast with a friendly message if this guest is already at their
    // event's per-guest photo limit — the real enforcement is a DB trigger
    // (can't be bypassed), this just avoids wasting a storage upload on a
    // photo that would get rejected anyway.
    stage = 'check_payment_and_photo_cap';
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('photo_cap_per_guest, is_paid, payment_status, created_by')
      .eq('id', eventId)
      .single();

    if (eventError) throw eventError;

    // Fail fast with a friendly message if the host hasn't finished paying
    // for this event yet — the real enforcement is the
    // enforce_event_paid_for_upload DB trigger (can't be bypassed), this
    // just avoids wasting a storage upload on a photo that would get
    // rejected anyway. The event's own creator is exempt.
    if (
      event &&
      event.payment_status !== 'free' &&
      event.payment_status !== 'completed' &&
      event.created_by !== userId
    ) {
      throw new Error(
        "This event isn't ready for uploads yet — the host still needs to complete payment."
      );
    }

    if (event?.photo_cap_per_guest != null) {
      const { count, error: countError } = await supabase
        .from('photos')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('uploaded_by', userId);

      if (countError) throw countError;

      if (count >= event.photo_cap_per_guest) {
        throw new Error(
          `You've reached the ${event.photo_cap_per_guest}-photo limit for this event.`
        );
      }
    }

    stage = 'normalize';
    let uploadFile;
    try {
      uploadFile = await normalizeImage(file);
    } catch (normalizeError) {
      // normalizeImage only touches HEIC/HEIF files (see imageValidation.js)
      // — but that canvas encode can still fail outright, or silently hand
      // back an empty result under memory pressure, a real observed
      // failure mode on mobile when several photos process at once. `file`
      // already passed validateImage() above, so it's known-good; fall
      // back to uploading it unmodified (as HEIC) rather than losing the
      // photo to a conversion-only bug. Logged so a spike in this is
      // visible without needing to reproduce it on a device.
      logError('uploadImage:normalizeFallback', normalizeError, { eventId, userId });
      uploadFile = file;
    }

    if (!uploadFile || uploadFile.size === 0) {
      // Belt-and-braces: should be unreachable (validateImage already
      // rejects an empty `file`, and the fallback above only ever uses
      // `file`), but never let a zero-byte body reach Supabase Storage —
      // its "No content provided" response is meaningless to a guest.
      throw new Error("This photo couldn't be processed. Please retake it and try again.");
    }

    const { width, height } = await getImageDimensions(uploadFile);

    // The original filename comes straight from the client and can contain
    // anything (slashes, unicode, control characters) — never build a
    // storage path out of it unsanitized.
    const safeName = uploadFile.name
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .slice(-100) || 'photo.jpg';
    const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${safeName}`;
    const storagePath = `${eventId}/${fileName}`;

    stage = 'storage_upload';
    const { data, error: uploadError } = await uploadWithRetry(() =>
      supabase.storage.from('event-photos').upload(storagePath, uploadFile, {
        cacheControl: '3600',
        upsert: false,
      })
    );

    if (uploadError) throw uploadError;

    stage = 'db_insert';
    const { error: dbError } = await supabase.from('photos').insert({
      event_id: eventId,
      uploaded_by: userId,
      storage_path: storagePath,
      file_name: uploadFile.name,
      file_size: uploadFile.size,
      mime_type: uploadFile.type,
      width,
      height,
      uploader_name: uploaderName,
    });

    if (dbError) {
      await supabase.storage
        .from('event-photos')
        .remove([storagePath]);

      throw dbError;
    }

    return {
      success: true,
      data: data,
      message: 'Image uploaded successfully',
    };
  } catch (error) {
    console.error('Upload failed:', error);
    logError('uploadImage', error, { stage, eventId, userId });
    return {
      success: false,
      error: error.message,
    };
  }
};

export const getEventPhotos = async (eventId, limit = 50, offset = 0) => {
  try {
    const { data, error } = await supabase
      .from('photos')
      .select('*')
      .eq('event_id', eventId)
      .order('uploaded_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return {
      success: true,
      data: data || [],
    };
  } catch (error) {
    console.error('Failed to fetch photos:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const deletePhoto = async (photoId, storagePath) => {
  try {
    const { error: storageError } = await supabase.storage
      .from('event-photos')
      .remove([storagePath]);

    if (storageError) throw storageError;

    const { error: dbError } = await supabase
      .from('photos')
      .delete()
      .eq('id', photoId);

    if (dbError) throw dbError;

    return {
      success: true,
      message: 'Photo deleted successfully',
    };
  } catch (error) {
    console.error('Delete failed:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

export const getPublicPhotoUrl = (storagePath) => {
  const { data } = supabase.storage
    .from('event-photos')
    .getPublicUrl(storagePath);

  return data?.publicUrl || null;
};
